import { BASE_URL } from '../constants';
import { getAuthHeader } from '../utils';

export async function fetchMedicalStudyList(page, pageSize, filters) {
  let url = new URL(`${BASE_URL}insurance/medical-study/list/`);
  url.searchParams.append('page', page);
  url.searchParams.append('page_size', pageSize);

  // Append filters to the URL
  for (const key in filters) {
    if (filters[key]) {
      url.searchParams.append(key, filters[key]);
    }
  }

  const res = await fetch(url.toString(), {
    headers: getAuthHeader(),
  });
  if (!res.ok) throw new Error('No se pudo cargar la lista de estudios');
  return res.json();
}

export async function fetchMedicalStudyByToken(token) {
  console.debug(`Fetching medical study with token: ${token}`);
  const res = await fetch(`${BASE_URL}insurance/medical-study/detail/${token}/`);
  if (!res.ok) {
    const error = new Error('No se pudo cargar el estudio');
    // 410: el enlace del estudio venció (90 días). El visor muestra "Estudio vencido".
    error.expired = res.status === 410;
    throw error;
  }
  return res.json();
}

export async function getMedicalStudyCounts(filters = {}) {
  try {
    const url = new URL(`${BASE_URL}insurance/medical-study/counts/`);

    // Mismos filtros que la tabla (fecha, centro médico, doctor, búsqueda,
    // etc.) para que las tarjetas coincidan con el total del listado.
    for (const key in filters) {
      if (filters[key]) {
        url.searchParams.append(key, filters[key]);
      }
    }

    const res = await fetch(url.toString(), {
      headers: {
        ...getAuthHeader(),
        'Content-Type': 'application/json',
      },
    });
    if (!res.ok) throw new Error('Error al obtener conteos de estudios');
    return await res.json();
  } catch (err) {
    console.error('getMedicalStudyCounts:', err);
    return null;
  }
}

export async function deleteMedicalStudy(studyId, observation) {
  const res = await fetch(`${BASE_URL}insurance/medical-study/${studyId}/delete`, {
    method: 'DELETE',
    headers: {
      ...getAuthHeader(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ observation }),
  });
  if (!res.ok) throw new Error('No se pudo eliminar el estudio');
  return true;
}

/**
 * Dispara el reprocesamiento DICOM de un estudio atascado desde el dashboard.
 * Solo accesible para admin / centro médico. Devuelve 202 Accepted.
 *
 * Útil cuando un estudio quedó con `processing_state='processing'` y nunca
 * terminó (ej. error "Orthanc upload failed or StudyInstanceUID missing").
 * El backend limpia los flags de intento fallido y reencola la tarea Celery
 * que sube a Orthanc, procesa DICOM y sincroniza WordPress.
 *
 * @param {number} studyId
 * @param {{ force?: boolean }} [options]
 * @returns {Promise<{success, skipped, enqueued, reason, study_id, uid_token}>}
 */
export async function reprocessStudy(studyId, options = {}) {
  const res = await fetch(`${BASE_URL}insurance/medical-study/${studyId}/reprocess/`, {
    method: 'POST',
    headers: {
      ...getAuthHeader(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ force: !!options.force }),
  });
  let data = {};
  try {
    data = await res.json();
  } catch {
    data = {};
  }
  if (!res.ok) {
    throw new Error(data.error || data.detail || 'No se pudo reprocesar el estudio');
  }
  return data;
}

// ---- MedicalStudyType endpoints ----
export async function fetchMedicalStudyTypes(filters = {}) {
  const url = new URL(`${BASE_URL}insurance/medical-study-types/`);
  if (!filters.medical_center_id) {
    throw new Error('medical_center_id es requerido');
  }
  for (const key in filters) {
    if (filters[key] !== undefined && filters[key] !== null && filters[key] !== '') {
      url.searchParams.append(key, filters[key]);
    }
  }

  const res = await fetch(url.toString(), {
    headers: getAuthHeader(),
  });
  if (!res.ok) throw new Error('No se pudieron cargar los tipos de estudio');
  return res.json();
}

export async function fetchMedicalStudyTypeById(id) {
  if (!id) throw new Error('id is required');
  const res = await fetch(`${BASE_URL}insurance/medical-study-types/${id}/`, {
    headers: getAuthHeader(),
  });
  if (!res.ok) throw new Error('No se pudo cargar el tipo de estudio');
  return res.json();
}

export async function createMedicalStudyType(data = {}) {
  const res = await fetch(`${BASE_URL}insurance/medical-study-types/`, {
    method: 'POST',
    headers: {
      ...getAuthHeader(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('No se pudo crear el tipo de estudio');
  return res.json();
}

export async function updateMedicalStudyType(id, data = {}) {
  if (!id) throw new Error('id is required');
  const res = await fetch(`${BASE_URL}insurance/medical-study-types/${id}/`, {
    method: 'PUT',
    headers: {
      ...getAuthHeader(),
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  if (!res.ok) throw new Error('No se pudo actualizar el tipo de estudio');
  return res.json();
}

export async function deleteMedicalStudyType(id) {
  if (!id) throw new Error('id is required');
  const res = await fetch(`${BASE_URL}insurance/medical-study-types/${id}/`, {
    method: 'DELETE',
    headers: getAuthHeader(),
  });
  if (!res.ok) throw new Error('No se pudo eliminar el tipo de estudio');
  return true;
}

export async function importMedicalStudyTypes(file, medical_center_id) {
  if (!file) throw new Error('file is required');
  if (!medical_center_id) throw new Error('medical_center_id es requerido');
  const formData = new FormData();
  formData.append('file', file);
  formData.append('medical_center_id', medical_center_id);

  const res = await fetch(`${BASE_URL}insurance/medical-study-types/import/`, {
    method: 'POST',
    headers: getAuthHeader(),
    body: formData,
  });
  if (!res.ok) throw new Error('No se pudo importar el listado de estudios');
  return res.json();
}

/**
 * FEATURE: Obtiene el JSON crudo de un estudio desde el endpoint
 * /insurance/medical-study/{id}/.
 *
 * Útil cuando el consumidor necesita la respuesta completa (incluyendo
 * campos de modelo como `dictado_status`, `report_ready_at`, etc.) en lugar
 * del payload normalizado que ofrece `fetchStudyOriginalData`.
 *
 * Acepta las mismas opciones que `fetch`:
 *   - `silent`: si true, NO lanza error cuando la respuesta no es OK y
 *     devuelve `null`. Útil para llamadas de "refrescar en background".
 *
 * @param {string|number} studyId - ID o token del estudio
 * @param {Object} [options]
 * @param {boolean} [options.silent=false] - Si true, devuelve null en error.
 * @returns {Promise<Object|null>} Respuesta cruda del backend, o null si
 *   silent=true y la petición falla.
 */
export async function fetchStudyDetail(studyId, options = {}) {
  if (!studyId) {
    if (options.silent) return null;
    throw new Error('studyId es requerido');
  }

  const url = `${BASE_URL}insurance/medical-study/${studyId}/`;
  const res = await fetch(url, { headers: getAuthHeader() });

  if (!res.ok) {
    if (options.silent) return null;
    const errorText = await res.text();
    throw new Error(`Error ${res.status}: ${errorText}`);
  }

  return res.json();
}

/**
 * Obtiene datos originales de un estudio desde el endpoint existente
 * @param {string|number} studyId - ID o token del estudio
 * @returns {Promise<Object>} Datos normalizados del paciente y estudio
 */
export async function fetchStudyOriginalData(studyId) {
  if (!studyId) {
    throw new Error('studyId es requerido');
  }

  const url = `${BASE_URL}insurance/medical-study/${studyId}/`;

  const res = await fetch(url, {
    headers: getAuthHeader(),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(`Error ${res.status}: ${errorText}`);
  }

  const result = await res.json();
  const studyData = result.data || {};
  const patientData = studyData.Patient || {};
  const studyInfo = studyData.Study || {};

  // Retornar datos normalizados con estructura envuelta
  return {
    success: true,
    data: {
      // Datos del paciente
      nombre: studyData.full_name || patientData.Name || 'N/A',
      cedula: studyData.ci || patientData.ID || 'N/A',
      sexo: studyData.sex || patientData.Sex || 'N/A',
      edad: patientData.Age || '',
      fecha_nacimiento: studyData.birth_date || '',

      // Datos del estudio
      estudio: studyData.study_name_type || studyInfo.Modality || 'N/A',
      fecha_estudio: studyInfo.Date || '',
      descripcion: studyInfo.Description || '',

      // Metadatos útiles
      external_id: studyData.external_id || '',
      external_url: studyData.external_url || '',

      // Estado del estudio (viene como campo de modelo, NO dentro de data).
      // Lo exponemos para que el frontend pueda mostrar acciones según el estado
      // (ej. botón "Devolver a Pendiente" solo si está en informe_generado).
      dictado_status: result.dictado_status || '',

      // ID del médico asignado (viene del top-level de la respuesta y también
      // se inyecta en `data` por el backend). Lo exponemos en el wrapper para
      // que la normalización de GenerateReport lo encuentre aunque `_raw` aún
      // no se haya actualizado en cache.
      doctor_id: result.doctor_id ?? null,

      // Datos completos por si se necesitan (debugging futuro)
      _raw: studyData,
    },
    source: 'medical_study_detail',
  };
}
